import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Upload,
  Loader2,
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
  GripVertical,
  Star,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useDragSessionGuard } from "@/hooks/useDragSessionGuard";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toggleRowHighlight } from "@/lib/lineupHighlight";
import { prepareImageForUpload } from "@/lib/imageForUpload";
import {
  resolveExtractedStageIds,
  mergeDetectedStages,
  type ExtractedRow,
} from "@/lib/multiStageLineup";

type ImportMode = "ai" | "multi" | "manual";

// An AI-extracted artist whose stage is known but whose set time couldn't be
// read (and couldn't be derived from a neighbouring act) — the producer fills
// the missing time(s) in by hand. Times may be partially prefilled.
type IncompleteRow = {
  artist_name: string;
  stage_name: string;
  set_start: string;
  set_end: string;
};

// Short "how it works" blurb shown under the mode toggle. Keeps the three
// import modes self-explanatory without opening the guide.
const MODE_DESCRIPTIONS: Record<ImportMode, string> = {
  ai: "Upload one screenshot per stage — AI extracts that stage's artists and set times.",
  multi:
    "Upload one screenshot of the whole day — AI sorts the artists across your stages automatically.",
  manual: "Enter artists and set times yourself, stage by stage.",
};

interface LineupImportTabProps {
  day: Date;
  stages: Array<{ day: string; name: string; id?: string | null }>;
  timetable: Array<{
    event_date: string;
    stage_name: string;
    stage_id?: string | null;
    artist_name: string;
    set_start: string;
    set_end: string;
    highlighted?: boolean;
  }>;
  onTimetableUpdate: (
    timetable: Array<{
      event_date: string;
      stage_name: string;
      stage_id?: string | null;
      artist_name: string;
      set_start: string;
      set_end: string;
      highlighted?: boolean;
    }>,
  ) => void;
  // Called BEFORE an arrow-button swap is applied so the parent can
  // preview the assignment-rebind impact and gate behind a
  // confirmation dialog. The component does not call
  // onTimetableUpdate itself for arrow swaps; the parent decides
  // whether to apply via the supplied apply() callback.
  onMoveRequest?: (params: {
    proposedTimetable: Array<{
      event_date: string;
      stage_name: string;
      artist_name: string;
      set_start: string;
      set_end: string;
    }>;
    fromArtist: string;
    toArtist: string;
    stageName: string;
    eventDate: string;
  }) => void;
  onAiExtractionUsed?: () => void;
}

export function LineupImportTab({
  day,
  stages,
  timetable,
  onTimetableUpdate,
  onMoveRequest,
  onAiExtractionUsed,
}: LineupImportTabProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [screenshots, setScreenshots] = useState<{ [stageName: string]: File | null }>({});
  const [mode, setMode] = useState<ImportMode>("ai");
  const [selectedStage, setSelectedStage] = useState(stages[0]?.name || "");
  // Multi AI Upload: one whole-day screenshot. Plus the artists whose set time
  // the AI couldn't read and couldn't be reconciled from a neighbour — the
  // producer enters those by hand before they join the timetable.
  const [multiScreenshot, setMultiScreenshot] = useState<File | null>(null);
  const [incomplete, setIncomplete] = useState<IncompleteRow[]>([]);

  // Look up the briefing_stages.id for a given stageName slug. The
  // `stages` prop carries `id` post-migration 20260504240200; the
  // filter helpers below prefer FK match (timetable.stage_id ↔
  // stage.id) so a producer renaming a stage in step 1 between
  // imports doesn't strand timetable rows.
  const stageIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stages) {
      if (s.id) map.set(s.name, s.id);
    }
    return map;
  }, [stages]);
  const matchesTimetableStage = (
    t: { stage_name: string; stage_id?: string | null; event_date: string },
    stageName: string,
    eventDate: string,
  ): boolean => {
    if (t.event_date !== eventDate) return false;
    const expectedId = stageIdByName.get(stageName);
    if (expectedId && t.stage_id) return t.stage_id === expectedId;
    return t.stage_name === stageName;
  };
  // Tracks which lineup row is currently being dragged. Cleared on
  // drop or dragend. Same-stage only — cross-stage drag is out of
  // scope (set times wouldn't carry).
  const [draggedRow, setDraggedRow] = useState<{ index: number; stageName: string } | null>(null);
  // A browser that abandons a drag without firing `dragend` would otherwise
  // leave the list stuck showing its drop indicator.
  const endRowDrag = () => setDraggedRow(null);
  useDragSessionGuard(draggedRow !== null, endRowDrag);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, stageName: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    setScreenshots({ ...screenshots, [stageName]: file });
  };

  const handleAIExtraction = async (stageName: string) => {
    const screenshot = screenshots[stageName];
    if (!screenshot) {
      toast.error("Please upload a screenshot first");
      return;
    }

    setIsProcessing(true);
    try {
      const { dataUrl: base64Image, lowResolution } = await prepareImageForUpload(screenshot);
      if (lowResolution) {
        toast.warning(
          "This screenshot is low-resolution — the AI may misread set times. A larger/sharper image works best.",
        );
      }

      // Call edge function - pass all available stages so AI can match any stage in the image
      const allStageNames = stages.map((s) => s.name);
      const { data, error } = await supabase.functions.invoke("extract-lineup", {
        body: {
          image: base64Image,
          stages: allStageNames,
          currentStage: stageName,
          date: format(day, "yyyy-MM-dd"),
        },
      });

      if (error) throw error;

      const lineup = (data?.lineup ?? []) as ExtractedRow[];
      const newIncomplete = (data?.incomplete ?? []) as IncompleteRow[];
      if (lineup.length > 0) {
        // Filter out existing entries for this stage and day, then add new ones
        const currentDayFormatted = format(day, "yyyy-MM-dd");
        const otherStageTimetable = timetable.filter(
          (t) => !matchesTimetableStage(t, stageName, currentDayFormatted),
        );
        onTimetableUpdate([...otherStageTimetable, ...lineup]);
      }
      mergeIncomplete(newIncomplete);

      if (lineup.length === 0 && newIncomplete.length === 0) {
        toast.error("No set times could be extracted from this screenshot");
        return;
      }
      onAiExtractionUsed?.(); // Notify parent that AI extraction was used
      toast.success(
        newIncomplete.length > 0
          ? `Extracted ${lineup.length} set time(s); ${newIncomplete.length} need a time entered`
          : `Extracted ${lineup.length} set times from screenshot`,
      );
    } catch (error: unknown) {
      console.error("Extract lineup error:", error);
      const msg =
        error instanceof Error ? error.message : "Failed to extract timetable from screenshot";
      toast.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMultiFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    setMultiScreenshot(file);
  };

  // Multi AI Upload: one screenshot covering the whole day across every stage.
  // The edge function reads each stage separately and returns rows grouped by
  // stage; we replace only the stages it detected (leaving others untouched) and
  // surface any artist whose time couldn't be read for manual entry.
  const handleMultiExtraction = async () => {
    if (!multiScreenshot) {
      toast.error("Please upload a screenshot first");
      return;
    }

    setIsProcessing(true);
    try {
      const { dataUrl: base64Image, lowResolution } = await prepareImageForUpload(multiScreenshot);
      if (lowResolution) {
        toast.warning(
          "This screenshot is low-resolution — the AI may misread set times. A larger/sharper image works best.",
        );
      }

      const allStageNames = stages.map((s) => s.name);
      const { data, error } = await supabase.functions.invoke("extract-lineup", {
        body: {
          image: base64Image,
          stages: allStageNames,
          multiStage: true,
          date: format(day, "yyyy-MM-dd"),
        },
      });

      if (error) throw error;

      const matched = resolveExtractedStageIds(
        (data?.lineup ?? []) as ExtractedRow[],
        stageIdByName,
      );
      const newIncomplete = (data?.incomplete ?? []) as IncompleteRow[];

      if (matched.length > 0) {
        onTimetableUpdate(mergeDetectedStages(timetable, matched));
      }
      mergeIncomplete(newIncomplete);

      if (matched.length === 0 && newIncomplete.length === 0) {
        toast.error("No set times could be extracted from this screenshot");
        return;
      }

      // Only flag AI extraction (which triggers the verify-timing disclaimer)
      // when the screenshot actually yielded something.
      onAiExtractionUsed?.();
      if (newIncomplete.length > 0) {
        toast.success(
          `Extracted ${matched.length} set time(s); ${newIncomplete.length} need a time entered`,
        );
      } else {
        toast.success(`Extracted ${matched.length} set time(s) across all stages`);
      }
    } catch (error: unknown) {
      console.error("Multi extract lineup error:", error);
      const msg =
        error instanceof Error ? error.message : "Failed to extract timetable from screenshot";
      toast.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Add freshly-returned incomplete rows, keeping any the producer is still
  // editing. Normalised to the IncompleteRow shape (blank times preserved).
  const mergeIncomplete = (rows: IncompleteRow[]) => {
    if (rows.length === 0) return;
    setIncomplete((prev) => [
      ...prev,
      ...rows.map((r) => ({
        artist_name: r.artist_name,
        stage_name: r.stage_name,
        set_start: r.set_start ?? "",
        set_end: r.set_end ?? "",
      })),
    ]);
  };

  const dropIncompleteAt = (index: number) => {
    setIncomplete((prev) => prev.filter((_, i) => i !== index));
  };

  const setIncompleteTime = (index: number, field: "set_start" | "set_end", value: string) => {
    setIncomplete((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  // Move a now-complete row into the timetable. Both times are required so a
  // blank set time can never reach the schedule (and trip the overlap check).
  const confirmIncompleteArtist = (index: number) => {
    const row = incomplete[index];
    if (!row) return;
    if (!row.set_start || !row.set_end) {
      toast.error("Please enter both a start and an end time");
      return;
    }
    onTimetableUpdate([
      ...timetable,
      {
        event_date: format(day, "yyyy-MM-dd"),
        stage_name: row.stage_name,
        stage_id: stageIdByName.get(row.stage_name) ?? null,
        artist_name: row.artist_name,
        set_start: row.set_start,
        set_end: row.set_end,
      },
    ]);
    dropIncompleteAt(index);
    toast.success(`${row.artist_name} added to ${row.stage_name}`);
  };

  // Discard an artist the producer doesn't want in the lineup at all.
  const removeIncompleteArtist = (index: number) => {
    const row = incomplete[index];
    if (!row) return;
    dropIncompleteAt(index);
    toast.success(`${row.artist_name} removed`);
  };

  const updateTimetableItem = (index: number, field: string, value: string, stageName: string) => {
    const currentDayFormatted = format(day, "yyyy-MM-dd");
    const stageTimetable = timetable.filter((t) =>
      matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    const newItem = { ...stageTimetable[index], [field]: value };
    const otherStageTimetable = timetable.filter(
      (t) => !matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    stageTimetable[index] = newItem;
    onTimetableUpdate([...otherStageTimetable, ...stageTimetable]);
  };

  const addTimetableItem = (stageName: string) => {
    const newItem = {
      event_date: format(day, "yyyy-MM-dd"),
      stage_name: stageName,
      // Carry the FK so subsequent filters match this row by id even
      // if the producer later renames the stage.
      stage_id: stageIdByName.get(stageName) ?? null,
      artist_name: "",
      set_start: "",
      set_end: "",
    };
    onTimetableUpdate([...timetable, newItem]);
  };

  const removeTimetableItem = (index: number, stageName: string) => {
    const currentDayFormatted = format(day, "yyyy-MM-dd");
    const stageTimetable = timetable.filter((t) =>
      matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    const otherStageTimetable = timetable.filter(
      (t) => !matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    stageTimetable.splice(index, 1);
    onTimetableUpdate([...otherStageTimetable, ...stageTimetable]);
  };

  // Flip the producer's "highlight this artist" flag. Highlighted
  // artists are rendered with an outer glow in the schedule creator
  // (CrewSchedule) so the important ones stand out while assigning
  // crew. Mirrors removeTimetableItem's filter/recombine so the index
  // lines up with the rows actually shown for this stage/day; the flip
  // itself is the pure, unit-tested toggleRowHighlight.
  const toggleHighlight = (index: number, stageName: string) => {
    const currentDayFormatted = format(day, "yyyy-MM-dd");
    const stageTimetable = timetable.filter((t) =>
      matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    const otherStageTimetable = timetable.filter(
      (t) => !matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    onTimetableUpdate([...otherStageTimetable, ...toggleRowHighlight(stageTimetable, index)]);
  };

  const moveItem = (index: number, direction: "up" | "down", stageName: string) => {
    const currentDayFormatted = format(day, "yyyy-MM-dd");
    const stageTimetable = timetable.filter((t) =>
      matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    const otherStageTimetable = timetable.filter(
      (t) => !matchesTimetableStage(t, stageName, currentDayFormatted),
    );

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= stageTimetable.length) return;

    const newStageTimetable = [...stageTimetable];
    // Only swap artist names, keep the times in the same position
    const tempArtistName = newStageTimetable[index].artist_name;
    newStageTimetable[index] = {
      ...newStageTimetable[index],
      artist_name: newStageTimetable[newIndex].artist_name,
    };
    newStageTimetable[newIndex] = { ...newStageTimetable[newIndex], artist_name: tempArtistName };

    const proposedTimetable = [...otherStageTimetable, ...newStageTimetable];

    // Defer to the parent's confirm dialog when wired, so the
    // producer sees the crew-assignment impact before the swap
    // takes effect. Falls back to direct apply when no handler is
    // supplied (keeps the component usable in isolation/tests).
    if (onMoveRequest) {
      onMoveRequest({
        proposedTimetable,
        fromArtist: stageTimetable[index].artist_name,
        toArtist: stageTimetable[newIndex].artist_name,
        stageName,
        eventDate: currentDayFormatted,
      });
      return;
    }
    onTimetableUpdate(proposedTimetable);
  };

  // Drag-drop reorder: pull the artist_name out of fromIndex and
  // insert it at toIndex; the set times stay anchored to row
  // positions, so all artists between from and to shift one slot.
  // Routes through the same onMoveRequest dialog as the arrow
  // buttons so the producer always sees the assignment impact
  // before the swap takes effect.
  const reorderArtists = (fromIndex: number, toIndex: number, stageName: string) => {
    if (fromIndex === toIndex) return;
    const currentDayFormatted = format(day, "yyyy-MM-dd");
    const stageTimetable = timetable.filter((t) =>
      matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    const otherStageTimetable = timetable.filter(
      (t) => !matchesTimetableStage(t, stageName, currentDayFormatted),
    );
    if (fromIndex < 0 || fromIndex >= stageTimetable.length) return;
    if (toIndex < 0 || toIndex >= stageTimetable.length) return;

    const artistNames = stageTimetable.map((s) => s.artist_name);
    const [moving] = artistNames.splice(fromIndex, 1);
    artistNames.splice(toIndex, 0, moving);
    const newStageTimetable = stageTimetable.map((s, i) => ({
      ...s,
      artist_name: artistNames[i],
    }));
    const proposedTimetable = [...otherStageTimetable, ...newStageTimetable];

    if (onMoveRequest) {
      onMoveRequest({
        proposedTimetable,
        fromArtist: stageTimetable[fromIndex].artist_name,
        toArtist: stageTimetable[toIndex].artist_name,
        stageName,
        eventDate: currentDayFormatted,
      });
      return;
    }
    onTimetableUpdate(proposedTimetable);
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Mode Toggle */}
        <div className="space-y-2" data-guide="step-2-tip-1">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={mode === "ai" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("ai")}
            >
              AI Upload
            </Button>
            <Button
              type="button"
              variant={mode === "multi" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("multi")}
            >
              Multi AI Upload
            </Button>
            <Button
              type="button"
              variant={mode === "manual" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("manual")}
            >
              Manual Mode
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {MODE_DESCRIPTIONS[mode]} You can switch between modes at any time during import.
          </p>
        </div>

        {/* Artists whose set time the AI couldn't read (and couldn't be
            derived from a neighbour) — the producer enters the time by hand. */}
        {incomplete.length > 0 && (
          <div className="space-y-3 rounded-lg border border-orange-500/30 bg-orange-500/5 p-4">
            <h3 className="text-sm font-semibold text-orange-600 dark:text-orange-400">
              {incomplete.length} artist(s) need a set time
            </h3>
            <p className="text-xs text-muted-foreground">
              The AI couldn't read these times clearly and they don't line up with a neighbouring
              act. Enter the missing time(s) — then add the artist to the timetable.
            </p>
            {incomplete.map((row, index) => (
              <div
                key={`${row.artist_name}-${index}`}
                className="grid grid-cols-12 items-center gap-2"
              >
                <div className="col-span-3 truncate text-sm font-medium">{row.artist_name}</div>
                <div className="col-span-2 truncate text-xs text-muted-foreground">
                  {row.stage_name}
                </div>
                <div className="col-span-2">
                  <Input
                    type="time"
                    aria-label={`Start time for ${row.artist_name}`}
                    value={row.set_start}
                    onChange={(e) => setIncompleteTime(index, "set_start", e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="time"
                    aria-label={`End time for ${row.artist_name}`}
                    value={row.set_end}
                    onChange={(e) => setIncompleteTime(index, "set_end", e.target.value)}
                  />
                </div>
                <div className="col-span-3 flex items-center justify-end gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => confirmIncompleteArtist(index)}
                  >
                    Add
                  </Button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove artist"
                        onClick={() => removeIncompleteArtist(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Remove from lineup</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Multi AI Upload — one whole-day screenshot across all stages */}
        {mode === "multi" && (
          <>
            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                ⚠️ <strong>Important:</strong> Upload one screenshot of the whole day's lineup. AI
                detects which artists belong to which stage using the stage names you entered
                earlier. Double-check all stages and timing — AI may make mistakes.
              </p>
            </div>
            <div className="space-y-4 rounded-lg border-2 border-dashed border-border p-6">
              <div className="flex items-center gap-4">
                <div className="flex flex-1 items-center gap-3">
                  <Label
                    htmlFor={`lineup-multi-upload-${format(day, "yyyy-MM-dd")}`}
                    className="inline-flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-all hover:scale-105 hover:shadow-glow"
                  >
                    Choose file
                  </Label>
                  <Input
                    id={`lineup-multi-upload-${format(day, "yyyy-MM-dd")}`}
                    type="file"
                    accept="image/*"
                    onChange={handleMultiFileUpload}
                    className="sr-only"
                    disabled={isProcessing}
                  />
                  <span className="truncate text-sm text-muted-foreground">
                    {multiScreenshot ? multiScreenshot.name : "No file chosen"}
                  </span>
                </div>
                <Button
                  onClick={handleMultiExtraction}
                  disabled={!multiScreenshot || isProcessing}
                  className="min-w-[140px]"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Layers className="mr-2 h-4 w-4" />
                      Extract all stages
                    </>
                  )}
                </Button>
              </div>
              {multiScreenshot && (
                <p className="text-sm text-muted-foreground">
                  Ready to process: {multiScreenshot.name}
                </p>
              )}
            </div>
          </>
        )}

        {/* Stage Tabs */}
        <Tabs value={selectedStage} onValueChange={setSelectedStage} className="w-full">
          <TabsList className="h-auto w-full flex-wrap justify-start">
            {stages.map((stage) => (
              <TabsTrigger key={stage.name} value={stage.name} className="min-w-[100px]">
                {stage.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {stages.map((stage) => {
            const currentDayFormatted = format(day, "yyyy-MM-dd");
            const stageTimetable = timetable.filter((t) =>
              matchesTimetableStage(t, stage.name, currentDayFormatted),
            );
            const stageScreenshot = screenshots[stage.name];

            return (
              <TabsContent key={stage.name} value={stage.name} className="space-y-4">
                {/* AI Upload Section */}
                {mode === "ai" && (
                  <>
                    <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
                      <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        ⚠️ <strong>Important:</strong> Please double-check all timing information as
                        AI may make mistakes. Verify all set times are accurate before publishing.
                      </p>
                    </div>
                    <div
                      className="space-y-4 rounded-lg border-2 border-dashed border-border p-6"
                      data-guide="step-2-tip-2"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex flex-1 items-center gap-3">
                          <Label
                            htmlFor={`lineup-upload-${stage.name}`}
                            className="inline-flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-all hover:scale-105 hover:shadow-glow"
                          >
                            Choose file
                          </Label>
                          <Input
                            id={`lineup-upload-${stage.name}`}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, stage.name)}
                            className="sr-only"
                            disabled={isProcessing}
                          />
                          <span className="truncate text-sm text-muted-foreground">
                            {stageScreenshot ? stageScreenshot.name : "No file chosen"}
                          </span>
                        </div>
                        <Button
                          onClick={() => handleAIExtraction(stage.name)}
                          disabled={!stageScreenshot || isProcessing}
                          className="min-w-[140px]"
                        >
                          {isProcessing ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Upload className="mr-2 h-4 w-4" />
                              Extract with AI
                            </>
                          )}
                        </Button>
                      </div>
                      {stageScreenshot && (
                        <p className="text-sm text-muted-foreground">
                          Ready to process: {stageScreenshot.name}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Timetable Entries */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Timetable for {stage.name}</h3>
                    <Button
                      onClick={() => addTimetableItem(stage.name)}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Set Time
                    </Button>
                  </div>

                  {stageTimetable.length === 0 ? (
                    <p className="py-8 text-center text-muted-foreground">
                      No timetable entries yet. Upload a screenshot or add manually.
                    </p>
                  ) : (
                    <div className="max-w-3xl space-y-3">
                      {/* Column Headers */}
                      <div className="grid grid-cols-12 items-center gap-2 text-sm font-semibold">
                        <div className="col-span-1"></div>
                        <div className="col-span-4">Artist</div>
                        <div className="col-span-3">Start Set</div>
                        <div className="col-span-2">End Set</div>
                        <div className="col-span-2"></div>
                      </div>
                      {stageTimetable.map((item, index) => {
                        const isDragTarget =
                          draggedRow !== null &&
                          draggedRow.stageName === stage.name &&
                          draggedRow.index !== index;
                        return (
                          <div
                            key={index}
                            className={`grid grid-cols-12 items-center gap-2 rounded transition-colors ${
                              isDragTarget ? "bg-primary/5 ring-1 ring-primary/30" : ""
                            }`}
                            onDragOver={(e) => {
                              if (
                                draggedRow &&
                                draggedRow.stageName === stage.name &&
                                draggedRow.index !== index
                              ) {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                              }
                            }}
                            onDrop={(e) => {
                              if (
                                !draggedRow ||
                                draggedRow.stageName !== stage.name ||
                                draggedRow.index === index
                              ) {
                                return;
                              }
                              e.preventDefault();
                              reorderArtists(draggedRow.index, index, stage.name);
                              setDraggedRow(null);
                            }}
                          >
                            <div className="col-span-1 flex items-center gap-0.5">
                              <div
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = "move";
                                  setDraggedRow({ index, stageName: stage.name });
                                }}
                                onDragEnd={endRowDrag}
                                className="cursor-grab rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
                                title="Drag to reorder"
                              >
                                <GripVertical className="h-4 w-4" />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => moveItem(index, "up", stage.name)}
                                disabled={index === 0}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => moveItem(index, "down", stage.name)}
                                disabled={index === stageTimetable.length - 1}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="col-span-4">
                              <Input
                                value={item.artist_name}
                                onChange={(e) =>
                                  updateTimetableItem(
                                    index,
                                    "artist_name",
                                    e.target.value,
                                    stage.name,
                                  )
                                }
                                placeholder="Artist name"
                              />
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="time"
                                value={item.set_start}
                                onChange={(e) =>
                                  updateTimetableItem(
                                    index,
                                    "set_start",
                                    e.target.value,
                                    stage.name,
                                  )
                                }
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="time"
                                value={item.set_end}
                                onChange={(e) =>
                                  updateTimetableItem(index, "set_end", e.target.value, stage.name)
                                }
                              />
                            </div>
                            <div className="col-span-2 flex items-center justify-end gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Toggle
                                    type="button"
                                    aria-label="Highlight artist in schedule creator"
                                    pressed={Boolean(item.highlighted)}
                                    onPressedChange={() => toggleHighlight(index, stage.name)}
                                    variant="outline"
                                    size="sm"
                                    className="h-9 w-9 p-0 text-muted-foreground hover:bg-primary/10 hover:text-primary data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-primary"
                                  >
                                    <Star
                                      className={`h-4 w-4 ${item.highlighted ? "fill-current" : ""}`}
                                    />
                                  </Toggle>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Highlight artist in schedule creator
                                </TooltipContent>
                              </Tooltip>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeTimetableItem(index, stage.name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </TooltipProvider>
  );
}
